import { Injectable } from "@angular/core";
import { AiActionDialogComponent, IAiActionDialogData } from "@common/lib/chat-ai/ai-action-dialog/ai-action-dialog.component";
import { ChatAiService, DefaultSystemMessageWithMarkdown, IChatCompletionBindingModel } from "@common/lib/chat-ai/chat-ai.service";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { SimpleValueType } from "@common/ux/edit-simple-value-breeze-entity-dialog/edit-simple-value-breeze-entity-dialog.component";
import { Observable } from "rxjs";
import { CommonDataService } from "../data/common-data.service";
import ChangeTone from "./prompts/editor/ChangeTone.txt";
import ExpandStatement from "./prompts/editor/ExpandStatement.txt";
import ProofreadStatement from "./prompts/editor/ProofreadStatement.txt";
import ShortenStatement from "./prompts/editor/ShortenStatement.txt";
import SummariseStatement from "./prompts/editor/SummariseStatement.txt";

export enum ChatEditorTone {
    Professional = "Professional",
    Casual = "Casual",
    Straightforward = "Straightforward",
    Confident = "Confident",
    Friendly = "Friendly",
}

export interface IChatEditorActionParamDetails {
    // based off IEditSimpleEntity
    label: string;
    type: SimpleValueType;
    selectValues?: { text: string, value: any }[];
    selectOptional?: boolean;
}

export type IBaseChatEditorActionParameterType = Record<string, unknown>;
export type IChatEditorActionParameters<T extends IBaseChatEditorActionParameterType> = { text: string } & T;

export interface IChatEditorActionBase<T extends IBaseChatEditorActionParameterType = IBaseChatEditorActionParameterType> {
    type: string;
    displayText: string;
    paramDetails?: {
        [K in keyof T]: IChatEditorActionParamDetails;
    };
}

// Action with only callback (no requestModel)
interface IChatEditorActionWithCallback<T extends IBaseChatEditorActionParameterType = IBaseChatEditorActionParameterType>
    extends IChatEditorActionBase<T> {
    callback: (params: IChatEditorActionParameters<T>, action: IChatEditorAction<T>) => Observable<string>;
    requestModel?: never;
}

// Action with only requestModel (no callback)
interface IChatEditorActionWithRequestModel<T extends IBaseChatEditorActionParameterType = IBaseChatEditorActionParameterType>
    extends IChatEditorActionBase<T> {
    requestModel: Partial<IChatCompletionBindingModel>;
    callback?: never;
}

// Union type - must have at least one of callback or requestModel
export type IChatEditorAction<T extends IBaseChatEditorActionParameterType = IBaseChatEditorActionParameterType> =
    | IChatEditorActionWithCallback<T>
    | IChatEditorActionWithRequestModel<T>;

@Injectable({
    providedIn: "root",
})
export class AiEditorActionsService {
    constructor(
        private dialogService: AdaptCommonDialogService,
        private chatAiService: ChatAiService,
        private commonDataService: CommonDataService,
    ) {
    }

    public getEditorActions(): Record<string, IChatEditorAction> {
        return {
            summarise: {
                type: "summarise",
                displayText: "Summarise",
                requestModel: {
                    systemChatMessage: SummariseStatement,
                    // no user prompt provided, will just be {{text}} by default.
                },
            },
            proofread: {
                type: "proofread",
                displayText: "Proofread",
                requestModel: {
                    systemChatMessage: ProofreadStatement,
                    // no user prompt provided, will just be {{text}} by default.
                },
            },
            expand: {
                type: "expand",
                displayText: "Expand",
                requestModel: {
                    systemChatMessage: ExpandStatement,
                    // no user prompt provided, will just be {{text}} by default.
                },
            },
            shorten: {
                type: "shorten",
                displayText: "Shorten",
                requestModel: {
                    systemChatMessage: ShortenStatement,
                    // no user prompt provided, will just be {{text}} by default.
                },
            },
            changeTone: {
                type: "changeTone",
                displayText: "Change tone",
                paramDetails: {
                    tone: {
                        label: "Tone",
                        type: SimpleValueType.Select,
                        selectValues: Object.values(ChatEditorTone)
                            .map((tone) => ({ text: tone, value: tone.toLocaleLowerCase() })),
                        selectOptional: false,
                    },
                },
                requestModel: {
                    // tone parameter will automatically be passed in as {{tone}} to the prompt message
                    systemChatMessage: ChangeTone,
                    // no user prompt provided, will just be {{text}} by default.
                },
            } as IChatEditorAction<{ tone: ChatEditorTone }>,
            askAI: {
                type: "askAI",
                displayText: "Ask AI",
                paramDetails: {
                    prompt: {
                        label: "Prompt",
                        type: SimpleValueType.TextArea,
                    },
                },
                requestModel: {
                    systemChatMessage: DefaultSystemMessageWithMarkdown,
                    userChatMessage: `Text: "{{text}}".\n{{prompt}}`,
                },
            },
        };
    }

    public openAiActionDialog<T extends IBaseChatEditorActionParameterType>(data: IAiActionDialogData<T>) {
        return this.dialogService.open(AiActionDialogComponent, data);
    }

    public executeEditorAction<T extends Record<string, any>>(action: IChatEditorAction<T>, params: IChatEditorActionParameters<T>) {
        if (action.callback) {
            return action.callback(params, action);
        }

        if (action.requestModel?.systemChatMessage) {
            return this.chatAiService.getPromptCompletion({
                organisationId: this.commonDataService.getOrganisationId(),
                topic: action.displayText,
                systemChatMessage: this.chatAiService.processPrompt(action.requestModel.systemChatMessage, params),
                userChatMessage: this.chatAiService.processPrompt(action.requestModel.userChatMessage ?? params.text, params),
            });
        }

        throw new Error("Malformed editor action. No callback or valid requestModel provided.");
    }

}
